import Decimal from 'decimal.js';

/**
 * All monetary values in this application are represented as integer cents in
 * TypeScript and `numeric(14,2)` in Postgres. Floating point arithmetic is never
 * used for money.
 */
export type Cents = number;

Decimal.set({ precision: 28, rounding: Decimal.ROUND_HALF_UP });

/** Markup rates are stored as a decimal fraction with 4 places, e.g. 9.00% => "0.0900". */
export type MarkupRate = string;

export const MAX_CENTS = 99_999_999_999_99;

export function isValidCents(value: unknown): value is Cents {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= MAX_CENTS;
}

/** Parses a user-entered dollar string ("1,234.56") into integer cents. */
export function parseDollarsToCents(input: string): Cents {
  const cleaned = input.trim().replace(/[$,\s]/g, '');
  if (cleaned === '') return 0;
  if (!/^-?\d*(\.\d{0,2})?$/.test(cleaned)) {
    throw new Error(`Invalid currency value: ${input}`);
  }
  const cents = new Decimal(cleaned).times(100);
  if (!cents.isInteger()) {
    throw new Error(`Currency values may not have more than two decimal places: ${input}`);
  }
  return cents.toNumber();
}

/** Formats integer cents as a dollar string with exactly two decimal places. */
export function formatCents(cents: Cents): string {
  return new Decimal(cents).dividedBy(100).toFixed(2);
}

/** Formats integer cents for display, e.g. "$4,325.16". */
export function formatCurrency(cents: Cents): string {
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const whole = Math.trunc(abs / 100);
  const fraction = String(abs % 100).padStart(2, '0');
  const grouped = whole.toLocaleString('en-US');
  return `${negative ? '-' : ''}$${grouped}.${fraction}`;
}

/** Converts a `numeric(14,2)` string from Postgres into integer cents. */
export function numericToCents(value: string | number | null | undefined): Cents {
  if (value === null || value === undefined) return 0;
  return new Decimal(value).times(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();
}

/** Converts integer cents into a `numeric(14,2)` string for Postgres. */
export function centsToNumeric(cents: Cents): string {
  return new Decimal(cents).dividedBy(100).toFixed(2);
}

/** Multiplies a quantity by a unit price (in cents), rounding half-up to the cent. */
export function extendLineItem(quantity: string | number, unitPriceCents: Cents): Cents {
  return new Decimal(quantity)
    .times(unitPriceCents)
    .toDecimalPlaces(0, Decimal.ROUND_HALF_UP)
    .toNumber();
}

export function sumCents(values: readonly Cents[]): Cents {
  return values.reduce((total, value) => total + value, 0);
}

/** Parses a percentage entered by an administrator ("9" or "9.00") into a rate string. */
export function percentToRate(percent: string | number): MarkupRate {
  const decimal = new Decimal(percent).dividedBy(100);
  if (decimal.isNegative() || decimal.greaterThan(1)) {
    throw new Error(`Markup percentage must be between 0 and 100: ${percent}`);
  }
  return decimal.toFixed(4);
}

export function rateToPercentDisplay(rate: MarkupRate): string {
  return new Decimal(rate).times(100).toFixed(2);
}

export interface ProposalTotals {
  subtotalCents: Cents;
  markupRate: MarkupRate;
  markupCents: Cents;
  totalCents: Cents;
}

/**
 * The single authoritative money calculation in the application.
 *
 * Rounding happens exactly once, on the markup line, half-up to the nearest
 * cent. The total is then an exact integer sum, which is what the database
 * CHECK constraint asserts.
 */
export function computeProposalTotals(
  costItemAmounts: readonly Cents[],
  markupRate: MarkupRate,
): ProposalTotals {
  for (const amount of costItemAmounts) {
    if (!isValidCents(amount)) {
      throw new Error(`Invalid cost item amount: ${String(amount)}`);
    }
  }
  const subtotalCents = sumCents(costItemAmounts);
  const markupCents = new Decimal(subtotalCents)
    .times(markupRate)
    .toDecimalPlaces(0, Decimal.ROUND_HALF_UP)
    .toNumber();
  return {
    subtotalCents,
    markupRate,
    markupCents,
    totalCents: subtotalCents + markupCents,
  };
}
